import React, { useEffect } from 'react';
import dayjs from 'dayjs';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as pendingTransfersInitialState } from '~redux/slices/approval';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import * as Styles from './_projectApprovalTableSearchAndFilterBar.module.scss';
import { TPendingTransfersApprovalListingFilters } from '~types/feat/approval';

const TransfersApprovalSearchAndFilterBar = () => {
    const filters = useAppSelector(state => state.approval.pendingTransferListing.filters);
    const branchesList = useAppSelector((state) => state.branch.allBranches);

    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const initialFilters = pendingTransfersInitialState.pendingTransferListing.filters;

    const dispatch = useAppDispatch();

    const setFilters = (inputFilters: TPendingTransfersApprovalListingFilters) => dispatch(combinedActions.approvalSetpendingTransferListingFilters(inputFilters));
    const handleSearchClick = () => dispatch(combinedActions.approvalGetPendingTransferListingAttempt());

    useEffect(() => {
        setFilters({
            ...filters,
        });
    }, []);

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            setFilters({
                ...filters,
                branchCode,
                branchName: branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '',
            });
        }
    }, [branchesList]);

    const handleCancelClick = () => {
        setFilters({
            ...initialFilters,
        });
        dispatch(combinedActions.approvalGetPendingTransferListingAttempt());
    };

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];
        if (!allBranchAccess){
            return branchesList
                .filter(branch => branch.branchCode === branchCode)
                .map((branch) => ({
                    label: branch.branchName,
                    value: branch.branchId,
                }));
        }
        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchId,
        }));
    };

    // Source branch display value
    const sourceValue = branchesList.find(b => b.branchId === filters.branchFrom)?.branchName || '';

    // Target branch display value
    const targetValue = branchesList.find(b => b.branchId === filters.branchTo)?.branchName || '';

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Source Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: sourceValue, // <-- display computed name
                setValue: (event) => {
                    const selectedBranch = branchesList.find(b => b.branchName === event.target.value);
                    if (!selectedBranch) return;
                    setFilters({
                        ...filters,
                        branchFrom: selectedBranch.branchId,
                        branchTo: 0, // reset target
                    });
                },
                optionSelected: !!filters.branchFrom,
                noOptionsMessage: 'No branch found',
            },
        },
        {
            label: 'Target Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: targetValue,
                setValue: (event) => {
                    const selectedBranch = branchesList.find(b => b.branchName === event.target.value);
                    if (!selectedBranch) return;
                    setFilters({
                        ...filters,
                        branchTo: selectedBranch.branchId,
                        branchFrom: 0, // reset source
                    });
                },
                optionSelected: !!filters.branchTo,
                noOptionsMessage: 'No branch found',
            },
        },
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom,
                setDate: (date: string) => {
                    if (date > filters?.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else setFilters({ ...filters, dateFrom: date });
                },
                maxDate: dayjs().toISOString(),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsStart: true,
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo,
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({ ...filters, dateTo: date }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
            gridColumns={2}
        />
    );
};

export default TransfersApprovalSearchAndFilterBar;
